import React, { useMemo, useState } from 'react'
import { Anchor, Form, Spinner, Stack } from 'react-bootstrap'

import styled from 'styled-components'

import { Colours } from '@lib/theme'

import { Hint } from './Hint'
import { SearchTextField } from './SearchTextField'

export type FilterItem = {
  key: string
  displayName: string
  synonyms?: string[]
  type?: string
}

export interface Props {
  title: string
  items: FilterItem[]
  selectedKeys: (string | null)[]
  onSelectionChanged: (value: string[]) => void
  loading?: boolean
  showExpanderNum?: number
  itemsPerExpanderClick?: number
  showSearch?: boolean
  hint?: string
}

export function Filter({
  loading = false,
  title,
  items,
  selectedKeys,
  onSelectionChanged,
  showExpanderNum = 10,
  itemsPerExpanderClick = 10,
  showSearch = false,
  hint = '',
}: Props) {
  const [search, setSearch] = useState('')
  const displayItems = useMemo(() => {
    return items.filter((item) => {
      const stringIncludes = (string1: string, string2: string) =>
        string1.toLowerCase().includes(string2.toLowerCase().trim() ?? '')

      const filterByDisplayName = stringIncludes(item.displayName, search)

      const filterBySynonyms = item.synonyms?.some((synonym) =>
        stringIncludes(synonym, search)
      )

      return search ? filterByDisplayName || filterBySynonyms : true
    })
  }, [items, search])

  const [expanderNum, setExpanderNum] = useState(showExpanderNum)

  const showExpander = expanderNum > 0 && displayItems.length > expanderNum
  const remaining = Math.max(
    Math.min(displayItems.length - expanderNum, itemsPerExpanderClick),
    0
  )
  const elementId = title.replace(/[^a-zA-Z\d]+/g, '-').toLowerCase()

  function handleFilterChange(item: FilterItem) {
    const selection = [...selectedKeys]
    const index = selection.indexOf(item.key)
    if (index >= 0) {
      selection.splice(index, 1)
    } else {
      selection.push(item.key)
    }
    onSelectionChanged(
      selection.filter<string>(
        (key: string | null): key is string =>
          typeof key === 'string' && key.length > 0
      )
    )
  }

  const filterItems = useMemo(
    () =>
      getFilterItems(displayItems, {
        showExpander: showExpander,
        expanderNum: expanderNum,
      }),
    [displayItems, showExpander, expanderNum]
  )
  const extraItems = useMemo(
    () => getExtraItems(items, selectedKeys, filterItems),
    [items, selectedKeys, filterItems]
  )

  const genItemId = (key: string) =>
    (title.replace(/[^a-zA-Z\d]+/g, '_') + '_' + key).toLowerCase()

  return (
    <Stack gap={2} id={`filter-${elementId}`}>
      <TitleContainer>
        <h6>{title}</h6>

        {loading && <Spinner animation="border" size="sm" />}
        <Hint hint={hint} />
      </TitleContainer>

      {showSearch && (
        <CheckboxGroup>
          <SearchTextField
            placeholder="Search"
            aria-label={'Search ' + title + ' filters'}
            id={`search-${elementId}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="sm"
          />
        </CheckboxGroup>
      )}

      <CheckboxGroup>
        {items.length === 0 && (
          <p>
            <small>(None)</small>
          </p>
        )}

        {filterItems.map((item) => (
          <Form.Check
            key={item.key}
            id={genItemId(item.key)}
            label={item.displayName}
            checked={selectedKeys.includes(item.key)}
            onChange={(ev) => handleFilterChange(item)}
          />
        ))}

        {extraItems &&
          extraItems.map((item) => (
            <Form.Check
              key={item.key}
              id={genItemId(item.key)}
              label={item.displayName}
              checked={selectedKeys.includes(item.key)}
              onChange={(ev) => handleFilterChange(item)}
            />
          ))}

        {showExpander && (
          <Anchor
            href="#"
            onClick={() => setExpanderNum((num) => num + itemsPerExpanderClick)}
          >
            Load {remaining} more
          </Anchor>
        )}
      </CheckboxGroup>
    </Stack>
  )
}

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  height: 1.2rem;

  h6 {
    flex: 1 1 auto;
    margin: 0;
  }

  color: ${Colours.FgShade.Mid};
`

const CheckboxGroup = styled.div`
  margin-left: 0.25rem;

  label {
    width: 100%;
  }
`

export interface FilterDef {
  showExpander: boolean
  expanderNum: number
}

export function getFilterItems(
  displayItems: FilterItem[],
  { showExpander, expanderNum }: FilterDef
): FilterItem[] {
  return displayItems.filter((item, index) => {
    if (!showExpander) {
      return true
    }
    return index < expanderNum
  })
}

export function getExtraItems(
  items: FilterItem[],
  selectedKeys: Array<string | null>,
  focusedItems: FilterItem[]
): FilterItem[] {
  return items.filter((item) => {
    return (
      selectedKeys.includes(item.key) &&
      !focusedItems.find(({ key }) => key === item.key)
    )
  })
}
